/**
 * ZSystemConfig - Zod Validation Schemas for System Config API
 *
 * Single source of truth for System Config API validation.
 * These schemas generate both runtime validation (Zod) and compile-time types (TApi).
 *
 * Admin-panel endpoint - protected by AdminPermissionGuard (PBAC)
 * Base path: /api/admin/system-config
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import { CONFIG_DESCRIPTION_MAX_LENGTH, CronConfigSchema } from "./schemas/cron-config.schema";
import {
  CacheConfigSchema,
  CircuitBreakerConfigSchema,
  EchoExportsRetentionConfigSchema,
  RateLimitConfigSchema,
  RetryPolicyConfigSchema
} from "./schemas/operational-config.schema";
import {
  ConfigCategorySchema,
  SystemConfigHistorySchema,
  SystemConfigSchema
} from "./schemas/system-config.schema";
import {
  DeleteConfigByIdPathVarsSchema,
  DeleteConfigPathVarsSchema,
  GetConfigPathVarsSchema,
  GetHistoryPathVarsSchema,
  GetHistoryQuerySchema,
  ListConfigsQuerySchema,
  ResetConfigPathVarsSchema,
  UpsertConfigBodySchema
} from "./schemas/system-config-requests.schema";
import { UnreadableSystemConfigRowSchema } from "./schemas/system-config-row.schema";

export * from "./schemas/system-config.schema";
export * from "./schemas/system-config-requests.schema";

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS
// ═══════════════════════════════════════════════════════════════

/**
 * List Configs Response
 */
export const ListConfigsResponseSchema = z.object({
  configs: z.array(SystemConfigSchema),
  /** Matched rows that cannot be served as `configs` — see {@link UnreadableSystemConfigRowSchema}. */
  unreadable: z.array(UnreadableSystemConfigRowSchema),
  /** Every matched row: `configs.length + unreadable.length`. */
  total: z.number().int()
});

export type ListConfigsResponse = z.infer<typeof ListConfigsResponseSchema>;

/**
 * Get Config Response (includes typed config based on category)
 */
export const GetConfigResponseSchema = SystemConfigSchema;

export type GetConfigResponse = z.infer<typeof GetConfigResponseSchema>;

/**
 * Upsert Config Response
 */
export const UpsertConfigResponseSchema = SystemConfigSchema;

export type UpsertConfigResponse = z.infer<typeof UpsertConfigResponseSchema>;

/**
 * Delete Config Response
 */
export const DeleteConfigResponseSchema = z.object({
  deletedId: z.string().uuid()
});

export type DeleteConfigResponse = z.infer<typeof DeleteConfigResponseSchema>;

/**
 * Reset Config Response
 */
export const ResetConfigResponseSchema = SystemConfigSchema;

export type ResetConfigResponse = z.infer<typeof ResetConfigResponseSchema>;

/**
 * Get History Response
 */
export const GetHistoryResponseSchema = z.object({
  history: z.array(SystemConfigHistorySchema),
  total: z.number().int()
});

export type GetHistoryResponse = z.infer<typeof GetHistoryResponseSchema>;

// ═══════════════════════════════════════════════════════════════
// ZSYSTEM_CONFIG MANIFEST - Export all schemas for runtime use
// ═══════════════════════════════════════════════════════════════

/**
 * ZSystemConfig - Zod API validation schemas for System Config
 *
 * Single source of truth for System Config API:
 * - Runtime validation (backend + frontend)
 * - Type generation (TApi auto-inferred via InferSchemas)
 *
 * Admin endpoint - requires ROOT/admin authentication
 */
export const ZSystemConfig = {
  /**
   * List all system configs
   *
   * GET /api/admin/system-config
   *
   * Query params: category, enabled, search
   */
  List: {
    method: "GET" as const,
    path: "/api/admin/system-config",
    Query: ListConfigsQuerySchema,
    Response: ApiSuccessSchema(ListConfigsResponseSchema)
  },

  /**
   * List configs by category
   *
   * GET /api/admin/system-config/:category
   */
  ListByCategory: {
    method: "GET" as const,
    path: "/api/admin/system-config/:category",
    PathVars: z.object({ category: ConfigCategorySchema }),
    Query: z.object({
      // Custom boolean parser for query params (z.coerce.boolean() treats "false" as true!)
      enabled: z
        .union([z.boolean(), z.string()])
        .transform((val) => {
          if (typeof val === "boolean") return val;
          if (val === "true") return true;
          if (val === "false") return false;
          return undefined;
        })
        .optional(),
      search: z.string().max(255).optional()
    }),
    Response: ApiSuccessSchema(ListConfigsResponseSchema)
  },

  /**
   * Get a specific config by category and key
   *
   * GET /api/admin/system-config/:category/:key
   */
  Get: {
    method: "GET" as const,
    path: "/api/admin/system-config/:category/:key",
    PathVars: GetConfigPathVarsSchema,
    Response: ApiSuccessSchema(GetConfigResponseSchema)
  },

  /**
   * Create or update a config
   *
   * POST /api/admin/system-config/:category/:key
   *
   * Upsert semantics: creates if not exists, updates if exists.
   * Always creates a history entry.
   */
  Upsert: {
    method: "POST" as const,
    path: "/api/admin/system-config/:category/:key",
    PathVars: GetConfigPathVarsSchema,
    Body: UpsertConfigBodySchema,
    Response: ApiSuccessSchema(UpsertConfigResponseSchema)
  },

  /**
   * Delete a config
   *
   * DELETE /api/admin/system-config/:category/:key
   *
   * Soft delete behavior: history is preserved.
   */
  Delete: {
    method: "DELETE" as const,
    path: "/api/admin/system-config/:category/:key",
    PathVars: DeleteConfigPathVarsSchema,
    Response: ApiSuccessSchema(DeleteConfigResponseSchema)
  },

  /**
   * Delete one stored row by id
   *
   * DELETE /api/admin/system-config/rows/:id
   *
   * The only door to a row the category/key delete cannot address — one whose
   * category the code no longer knows, served by the list as `unreadable`.
   */
  DeleteById: {
    method: "DELETE" as const,
    path: "/api/admin/system-config/rows/:id",
    PathVars: DeleteConfigByIdPathVarsSchema,
    Response: ApiSuccessSchema(DeleteConfigResponseSchema)
  },

  /**
   * Reset config to code default
   *
   * POST /api/admin/system-config/:category/:key/reset
   *
   * Restores the configuration to its code default value from CRON_DEFAULTS.
   * Creates a history entry with "Reset to code default" reason.
   */
  Reset: {
    method: "POST" as const,
    path: "/api/admin/system-config/:category/:key/reset",
    PathVars: ResetConfigPathVarsSchema,
    Response: ApiSuccessSchema(ResetConfigResponseSchema)
  },

  /**
   * Get config change history
   *
   * GET /api/admin/system-config/:category/:key/history
   *
   * Returns all historical versions of this config.
   */
  GetHistory: {
    method: "GET" as const,
    path: "/api/admin/system-config/:category/:key/history",
    PathVars: GetHistoryPathVarsSchema,
    Query: GetHistoryQuerySchema,
    Response: ApiSuccessSchema(GetHistoryResponseSchema)
  }
} as const;

// Re-export config schemas for convenience
export {
  CacheConfigSchema,
  CircuitBreakerConfigSchema,
  CONFIG_DESCRIPTION_MAX_LENGTH,
  CronConfigSchema,
  EchoExportsRetentionConfigSchema,
  RateLimitConfigSchema,
  RetryPolicyConfigSchema
};

export type { CronConfig } from "./schemas/cron-config.schema";
export type {
  CacheConfig,
  CircuitBreakerConfig,
  EchoExportsRetentionConfig,
  RateLimitConfig,
  RetryPolicyConfig
} from "./schemas/operational-config.schema";
